import React, { useEffect, useRef, useState, useCallback } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Loader2, ArrowLeft, MoreVertical, BellOff, Bell, UserX, Flag, Phone, Video } from "lucide-react";
import { MessageBubble } from "@/components/community/MessageBubble";
import { Composer } from "@/components/community/Composer";
import { Avatar } from "@/components/community/ChatList";
import { FriendAddButton } from "@/components/social/FriendAddButton";
import { StarBadge } from "@/components/social/Stars";

/** Ventana de chat: historial, burbujas y barra de escritura. */
export const ChatWindow = ({ conv, me, isAdmin, onBack, typingName, bump, refreshList, onCall }) => {
  const [msgs, setMsgs] = useState(null);
  const [people, setPeople] = useState({});
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newIds, setNewIds] = useState([]);
  const [menu, setMenu] = useState(false);
  const [muted, setMuted] = useState(!!conv.muted);
  const endRef = useRef(null);

  const load = useCallback(async (markNew = false) => {
    try {
      const { data } = await userApi.get(`/community/conversations/${conv.conv_id}/messages`);
      setPeople(Object.fromEntries((data.people || []).map((p) => [p.key, p])));
      setMsgs((prev) => {
        if (markNew && prev) {
          const known = new Set(prev.map((m) => m.msg_id));
          setNewIds(data.messages.filter((m) => !known.has(m.msg_id)).map((m) => m.msg_id));
        }
        return data.messages;
      });
      await userApi.post(`/community/conversations/${conv.conv_id}/read`).catch(() => {});
      refreshList?.();
    } catch (e) {
      toast.error(apiError(e, "No se pudo abrir la conversación"));
      setMsgs([]);
    }
  }, [conv.conv_id, refreshList]);

  useEffect(() => { setMsgs(null); setNewIds([]); load(); }, [conv.conv_id, load]);
  useEffect(() => { if (bump) load(true); }, [bump, load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const remove = async (msg) => {
    try {
      await userApi.delete(`/community/messages/${msg.msg_id}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const forward = async (msg) => {
    try {
      await userApi.post(`/community/conversations/${conv.conv_id}/messages`,
        { forward_of: msg.msg_id, kind: msg.kind });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleMute = async () => {
    try {
      const { data } = await userApi.post(`/community/conversations/${conv.conv_id}/mute`);
      setMuted(data.muted);
      toast.success(data.muted ? "Conversación silenciada" : "Notificaciones activadas");
    } catch (e) { toast.error(apiError(e)); }
    setMenu(false);
  };

  const blockOther = async () => {
    const key = conv.people?.[0]?.key;
    if (!key) return;
    try {
      await userApi.post(`/community/block/${key}`);
      toast.success("Miembro bloqueado. Ya no podrá escribirte.");
      refreshList?.();
    } catch (e) { toast.error(apiError(e)); }
    setMenu(false);
  };

  const reportChat = async () => {
    const reason = window.prompt("Cuéntanos qué pasó (el administrador lo revisará):");
    if (!reason || reason.trim().length < 3) return setMenu(false);
    try {
      await userApi.post("/community/report",
        { target: conv.people?.[0]?.key || null, reason: reason.trim() });
      toast.success("Reporte enviado al administrador");
    } catch (e) { toast.error(apiError(e)); }
    setMenu(false);
  };

  const other = conv.people?.[0];
  const title = conv.type === "dm" ? (other?.name || "Chat") : conv.title;
  const locked = conv.admin_only && !isAdmin;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10"
        style={{ background: "rgba(10,3,4,0.5)" }}>
        <button data-testid="chat-back" onClick={onBack} className="lg:hidden text-white/60">
          <ArrowLeft className="w-5 h-5" />
        </button>
        {conv.type === "dm" ? <Avatar person={other} size={36} /> : null}
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-white text-sm font-semibold truncate" data-testid="chat-title">{title}</div>
            {conv.type === "dm" && <StarBadge level={other?.level} />}
          </div>
          <div className="text-[11px] text-white/45 truncate">
            {typingName ? <span className="text-red-400">{typingName} está escribiendo…</span>
              : conv.type === "channel" ? conv.description
              : conv.type === "group" ? `${conv.members?.length || 0} miembros`
              : other?.bio || "Chat privado"}
          </div>
        </div>
        <div className="ml-auto relative flex items-center gap-1">
          {conv.type === "dm" && (
            <>
              <FriendAddButton personKey={other?.key} name={other?.name} />
              <button data-testid="call-audio-btn" title="Llamar" aria-label="Llamada de voz"
                onClick={() => onCall?.(conv, "audio")}
                className="text-white/65 hover:text-red-300 p-2 rounded-xl transition-colors">
                <Phone className="w-4.5 h-4.5" />
              </button>
              <button data-testid="call-video-btn" title="Videollamada" aria-label="Videollamada"
                onClick={() => onCall?.(conv, "video")}
                className="text-white/65 hover:text-red-300 p-2 rounded-xl transition-colors">
                <Video className="w-4.5 h-4.5" />
              </button>
            </>
          )}
          <button data-testid="chat-menu-btn" onClick={() => setMenu((v) => !v)}
            className="text-white/60 hover:text-white p-1.5"><MoreVertical className="w-4.5 h-4.5" /></button>
          {menu && (
            <div className="absolute right-0 top-9 glass-card p-1.5 w-52 z-30" data-testid="chat-menu">
              <button data-testid="chat-mute" onClick={toggleMute}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/85 hover:bg-white/8">
                {muted ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                {muted ? "Activar notificaciones" : "Silenciar"}
              </button>
              {conv.type === "dm" && (
                <button data-testid="chat-block" onClick={blockOther}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/85 hover:bg-white/8">
                  <UserX className="w-3.5 h-3.5" /> Bloquear miembro
                </button>
              )}
              <button data-testid="chat-report" onClick={reportChat}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-rose-300 hover:bg-white/8">
                <Flag className="w-3.5 h-3.5" /> Reportar
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5" data-testid="chat-messages"
        style={{ background: "linear-gradient(to bottom, rgba(3,0,0,0.62), rgba(3,0,0,0.78))" }}>
        {msgs === null ? (
          <Loader2 className="w-6 h-6 animate-spin text-red-400 mx-auto mt-10" />
        ) : msgs.length === 0 ? (
          <div className="text-center text-white/45 text-sm mt-10">
            Aún no hay mensajes. Rompe el hielo 👋
          </div>
        ) : msgs.map((m) => (
          <MessageBubble key={m.msg_id} msg={m} me={me} people={people}
            isNew={newIds.includes(m.msg_id)}
            onReply={setReplyTo} onForward={forward} onEdit={setEditing} onDelete={remove} />
        ))}
        <div ref={endRef} />
      </div>

      <Composer convId={conv.conv_id} replyTo={replyTo} editing={editing} disabled={locked}
        onSent={load} onCancelReply={() => setReplyTo(null)} onCancelEdit={() => setEditing(null)} />
    </div>
  );
};

export default ChatWindow;
